import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useWarehouses } from '../../hooks/useWarehouses';
import { useInventory } from '../../hooks/useInventory';
import { Warehouse } from '../../types/warehouse';
import { db } from '../../lib/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import {
  TrashIcon,
  PencilSquareIcon,
  PlusCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import LoadingScreen from '../common/LoadingScreen';
import WarehouseForm from './WarehouseForm';
import { toast } from 'react-hot-toast';

interface WarehouseListProps {
  onCreateWarehouse?: () => void;
}

const WarehouseList: React.FC<WarehouseListProps> = ({ onCreateWarehouse }) => {
  const { t } = useLanguage();
  const { warehouses, loading, error, createWarehouse, updateWarehouse, deleteWarehouse } = useWarehouses();
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [warehouseToDelete, setWarehouseToDelete] = useState<Warehouse | null>(null);
  const [warehousesWithInventory, setWarehousesWithInventory] = useState<Set<string>>(new Set());
  const [checkingInventory, setCheckingInventory] = useState(false);

  // Készlet ellenőrzése minden raktárhoz
  useEffect(() => {
    const checkInventory = async () => {
      if (warehouses.length === 0) return;
      
      setCheckingInventory(true);
      try {
        const warehouseIds = warehouses.map(w => w.id);
        const inventoryRef = collection(db, 'inventory');
        
        // Lekérdezzük az összes készlet rekordot egyszerre
        const inventoryQuery = query(inventoryRef, where('warehouseId', 'in', warehouseIds));
        const inventorySnapshot = await getDocs(inventoryQuery);
        
        // Összegyűjtjük azokat a raktár ID-kat, amikben van készlet
        const warehouseIdsWithInventory = new Set<string>();
        inventorySnapshot.docs.forEach(doc => {
          const data = doc.data();
          if (data.quantity && data.quantity > 0) {
            warehouseIdsWithInventory.add(data.warehouseId);
          }
        });
        
        setWarehousesWithInventory(warehouseIdsWithInventory);
      } catch (err) {
        console.error('Hiba a készlet ellenőrzésekor:', err);
      } finally {
        setCheckingInventory(false);
      }
    };

    checkInventory();
  }, [warehouses]);

  // Ellenőrzi, hogy törölhető-e a raktár
  const canDeleteWarehouse = (warehouseId: string) => {
    return !warehousesWithInventory.has(warehouseId);
  };

  // Rendereljük a raktár státusz badge-et
  const renderStatusBadge = (isActive: boolean) => (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium shadow-sm ${
      isActive 
        ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400 border border-green-200 dark:border-green-700' 
        : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-600'
      }`}
    >
      {isActive 
        ? <CheckCircleIcon className="w-3.5 h-3.5 mr-1.5 text-green-600 dark:text-green-400" /> 
        : <XCircleIcon className="w-3.5 h-3.5 mr-1.5 text-gray-500 dark:text-gray-400" />}
      {isActive ? t('common.active') : t('common.inactive')}
    </span>
  );

  // Új raktár létrehozásának kezelése
  const handleCreateWarehouse = () => {
    setSelectedWarehouse(null);
    setIsFormOpen(true);
    if (onCreateWarehouse) {
      onCreateWarehouse();
    }
  };

  // External trigger for creating warehouse
  useEffect(() => {
    if (onCreateWarehouse) {
      // Make the function available externally
      (window as any).triggerCreateWarehouse = handleCreateWarehouse;
    }
    return () => {
      delete (window as any).triggerCreateWarehouse;
    };
  }, [onCreateWarehouse]);

  // Raktár szerkesztésének kezelése
  const handleEditWarehouse = (warehouse: Warehouse) => {
    setSelectedWarehouse(warehouse);
    setIsFormOpen(true);
  };

  // Törlés modal megnyitása
  const handleDeleteClick = (warehouse: Warehouse) => {
    setWarehouseToDelete(warehouse);
    setIsDeleteModalOpen(true);
  };

  // Raktár törlésének kezelése
  const handleDeleteWarehouse = async () => {
    if (!warehouseToDelete) return;
    
    try {
      await deleteWarehouse(warehouseToDelete.id);
      toast.success(t('warehouse.success.deleted'), {
        duration: 3000,
        position: 'bottom-center'
      });
      
      // Frissítjük a készlet ellenőrzést is
      setWarehousesWithInventory(prev => {
        const updated = new Set(prev);
        updated.delete(warehouseToDelete.id);
        return updated;
      });
    } catch (err) {
      console.error('Hiba a raktár törlésekor:', err);
      const errorMessage = err instanceof Error ? err.message : t('warehouse.error.delete');
      toast.error(errorMessage, {
        duration: 5000,
        position: 'bottom-center'
      });
    } finally {
      setIsDeleteModalOpen(false);
      setWarehouseToDelete(null);
    }
  };

  // Form bezárása
  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedWarehouse(null);
  };

  // Delete modal bezárása
  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setWarehouseToDelete(null);
  };

  // Raktár mentése
  const handleSaveWarehouse = async (data: any) => {
    try {
      if (selectedWarehouse) {
        await updateWarehouse(selectedWarehouse.id, data);
      } else {
        await createWarehouse(data);
      }
      setIsFormOpen(false);
      setSelectedWarehouse(null);
    } catch (err) {
      console.error('Hiba a raktár mentésekor:', err);
    }
  };

  return (
    <div className="space-y-4 w-full max-w-full flex-grow p-4">

      {loading || checkingInventory ? (
        <LoadingScreen />
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md w-full">
          {error}
        </div>
      ) : warehouses.length === 0 ? (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-md w-full">
          {t('warehouse.list.empty')}
        </div>
      ) : (
        <>
          {/* Desktop táblázat */}
          <div className="hidden lg:block bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden border border-gray-200 dark:border-gray-700 w-full">
            <div className="overflow-x-auto w-full">
              <table className="table-fixed min-w-full w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-5 py-3.5 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/3">
                      {t('warehouse.fields.name')}
                    </th>
                    <th className="px-5 py-3.5 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/6">
                      {t('warehouse.fields.code')}
                    </th>
                    <th className="px-5 py-3.5 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/4">
                      {t('warehouse.fields.location')}
                    </th>
                    <th className="px-5 py-3.5 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/6">
                      {t('warehouse.fields.status')}
                    </th>
                    <th className="px-5 py-3.5 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-24">
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {warehouses.map(warehouse => (
                    <tr key={warehouse.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-5 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                        {warehouse.name}
                        {warehouse.isDefault && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400">
                            {t('warehouse.default')}
                          </span>
                        )}
                        {warehousesWithInventory.has(warehouse.id) && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-400">
                            {t('warehouse.hasInventory')}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{warehouse.code}</td>
                      <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{warehouse.location || '-'}</td>
                      <td className="px-5 py-4 whitespace-nowrap text-sm text-center">
                        {renderStatusBadge(warehouse.isActive)}
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap text-sm text-center">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleEditWarehouse(warehouse)}
                            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 p-1 rounded-full hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
                          >
                            <PencilSquareIcon className="h-5 w-5" />
                          </button>
                          {canDeleteWarehouse(warehouse.id) && (
                            <button
                              onClick={() => handleDeleteClick(warehouse)}
                              className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 p-1 rounded-full hover:bg-red-50 dark:hover:bg-red-900/20"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobil kártyák */}
          <div className="lg:hidden space-y-4">
            {warehouses.map(warehouse => (
              <div
                key={warehouse.id}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden"
              >
                {/* Kártya fejléc */}
                <div className="p-4 border-b border-gray-100 dark:border-gray-700">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
                        {warehouse.name}
                      </h3>
                      
                      <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 mb-2">
                        <span className="font-medium mr-2">Kód:</span>
                        <span className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs font-mono">
                          {warehouse.code}
                        </span>
                      </div>

                      {warehouse.location && (
                        <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                          <span className="font-medium mr-2">Helyszín:</span>
                          <span>{warehouse.location}</span>
                        </div>
                      )}

                      {/* Állapot és címkék */}
                      <div className="flex flex-wrap gap-2">
                        {renderStatusBadge(warehouse.isActive)}
                        
                        {warehouse.isDefault && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400">
                            {t('warehouse.default')}
                          </span>
                        )}
                        
                        {warehousesWithInventory.has(warehouse.id) && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-400">
                            {t('warehouse.hasInventory')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="grid grid-cols-2 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => handleEditWarehouse(warehouse)}
                    className="flex flex-col items-center justify-center py-4 px-4 text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 border-r border-gray-200 dark:border-gray-700 transition-colors"
                  >
                    <PencilSquareIcon className="h-6 w-6 mb-1" />
                    <span className="text-sm font-medium">Szerkesztés</span>
                  </button>
                  
                  {canDeleteWarehouse(warehouse.id) ? (
                    <button
                      onClick={() => handleDeleteClick(warehouse)}
                      className="flex flex-col items-center justify-center py-4 px-4 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      <TrashIcon className="h-6 w-6 mb-1" />
                      <span className="text-sm font-medium">Törlés</span>
                    </button>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-4 px-4 text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-700/50">
                      <ExclamationTriangleIcon className="h-6 w-6 mb-1" />
                      <span className="text-sm font-medium text-center">Tartalmaz készletet</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Warehouse Form Modal */}
      {isFormOpen && (
        <WarehouseForm
          warehouse={selectedWarehouse}
          isOpen={isFormOpen}
          onClose={handleCloseForm}
          onSave={handleSaveWarehouse}
        />
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && warehouseToDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-black dark:bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative border shadow-lg rounded-lg bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 max-w-md w-full">
            <div className="p-6 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20 mb-4">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                {t('warehouse.list.delete')}
              </h3>
              <div className="mb-4">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  {t('warehouse.list.deleteConfirmation')}
                </p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded">
                  "{warehouseToDelete.name}"
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleCloseDeleteModal}
                  className="flex-1 px-4 py-3 sm:py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleDeleteWarehouse}
                  className="flex-1 px-4 py-3 sm:py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  {t('common.delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarehouseList; 